import { useEffect, useRef, useState } from 'react';
import { Spin, App } from 'antd';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useAuthStore } from '@/features/auth/store/auth';
import { ChangeAppReceiver } from '@/shared/lib/change-app';
import { isSsoFederationAllowed } from '@/shared/config/featureFlags';
import './MinitokenGate.css';

interface MinitokenGateProps {
  children: React.ReactNode;
}

/**
 * MinitokenGate
 *
 * Global inbound change-app receiver. Other portals (PDT, Portal ID) send
 * users here the same way we send users to them: a redirect carrying
 * `?minitoken&originClientId&userId` query params — and the federated (SAML)
 * flow uses the same mechanism on its callback. This gate mirrors, on the
 * receiving side, what those portals do when they receive our minitoken:
 *
 * 1. Detects a `minitoken` on ANY entry URL (before route guards run)
 * 2. Exchanges it via `POST /user-identities/renew-app-token?clientId=...`
 *    (body: `validationToken`, `originClientId`, `userId`) for a full JWT
 * 3. Persists the session (`useAuthStore.loginCompleted`) and strips the
 *    token params from the URL, then renders the app at the original path
 *
 * While the exchange is in flight the routes are NOT rendered, so neither
 * PublicRoute nor PrivateRoute can bounce the user before the session
 * exists. On failure the app renders normally (the user falls back to the
 * login screen) with an error notification.
 *
 * The dedicated `/sso/callback` route is skipped — {@link SsoCallbackPage}
 * owns that path with a richer error UI. The whole gate is disabled when
 * the `VITE_SSO_FEDERATION_ALLOWED` feature flag is off — a deliberate
 * decision: inbound cross-portal reception ships in the same rollout wave
 * as the federated login, under a single switch. `rememberMe: true` follows
 * the federation spec (renew-app-token with remember-me=true).
 *
 * @param props - MinitokenGate component props
 * @returns The children, gated behind the inbound minitoken exchange
 * @since 1.0.0
 * @category Router
 */
const MinitokenGate: React.FC<MinitokenGateProps> = ({ children }) => {
  const { t } = useLanguage();
  const { notification } = App.useApp();
  const { pathname } = useLocation();

  const isDedicatedCallback = pathname.startsWith('/sso/callback');
  const shouldExchange =
    isSsoFederationAllowed() &&
    !isDedicatedCallback &&
    ChangeAppReceiver.hasMinitoken();

  const [exchanging, setExchanging] = useState(shouldExchange);
  // Defeat React 19 StrictMode double-effect: the minitoken is single-use,
  // so the exchange must run exactly once.
  const hasCalledApi = useRef(false);

  useEffect(() => {
    if (!shouldExchange || hasCalledApi.current) return;
    hasCalledApi.current = true;

    const receiver = new ChangeAppReceiver({
      bffBaseUrl: import.meta.env?.VITE_API_BASE_URL ?? '',
    });

    const exchangeToken = async (): Promise<void> => {
      try {
        const auth = await receiver.renewFromUrl({
          clientId: import.meta.env?.VITE_SSO_CLIENT_ID ?? '',
          originClientId:
            ChangeAppReceiver.extractOriginClientId() ?? undefined,
          userId: ChangeAppReceiver.extractUserId() ?? undefined,
          rememberMe: true,
        });

        useAuthStore.getState().loginCompleted({
          accessToken: auth.accessToken,
          expiresIn: auth.expiresIn,
          tokenType: auth.tokenType,
          scope: auth.scope ?? [],
        });
      } catch {
        notification.error({ message: t('SSO sign in failed') });
      } finally {
        setExchanging(false);
      }
    };

    void exchangeToken();
  }, [shouldExchange, notification, t]);

  if (exchanging) {
    return (
      <div
        className="minitoken-gate-loading"
        role="status"
        aria-label={t('Completing sign in')}
      >
        <Spin size="large" />
      </div>
    );
  }

  return <>{children}</>;
};

export default MinitokenGate;
